/**
 * cm-signup.js — emamaco first-party signup (Klaviyo signup replacement, P2).
 * Self-contained: styles injected, no dependencies. Three surfaces, one engine:
 *   1. Cart-drawer inline form — scv-drawer.js calls window.__cmSignup.inlineHtml()
 *      inside the Mum Club box; handlers here are DELEGATED so drawer re-renders
 *      never lose them.
 *   2. Modal (V2 "Unlock" copy, two-step: email+consent banked first, then optional
 *      name / due date / SMS) — opened by the popup triggers and the sticky tab.
 *   3. Sticky left-edge tab ("get 80% off").
 * Popup triggers: PRODUCT PAGES ONLY, after 15s on site OR exit-intent (desktop).
 * Caps: never after signup; 30-day snooze after dismiss; once per page load.
 * PRICING IS A NO-OP: the form presents as the unlock; pricing applies as today.
 * Events: CVR section_viewed/email_capture (vid/sid-less, endpoint tolerates) +
 * Meta Pixel Lead on success (replaces the old Klaviyo klaviyoForms listener).
 */
(function () {
  'use strict';
  if (window.__cmSignup) return;

  var API = 'https://api.convomesh.com/collect/emamaco-signup';
  var CVR = 'https://api.convomesh.com/collect/emamaco-cvr';
  var LS_DONE = 'cm_mc_signup_done';
  var LS_SNOOZE = 'cm_mc_signup_snooze';
  var SNOOZE_MS = 30 * 24 * 3600 * 1000;
  var CONSENT_TEXT_INLINE = 'Email me offers & drops';
  var CONSENT_TEXT_MODAL = 'Email me Emamaco offers, drops and my member pricing. Unsubscribe anytime.';
  var CONSENT_TEXT_SMS = 'text me sales, drops and order updates so I never miss out';
  // Universal SMS disclosure written to the strictest standard we sell into (US TCPA/CTIA);
  // also satisfies AU Spam Act / NZ UEMA / UK PECR, so one text covers every geo.
  var SMS_DISCLAIMER = 'By opting in to texts you agree to receive recurring automated marketing messages from Emamaco at the number provided. Consent is not a condition of purchase. Msg & data rates may apply, message frequency varies. Reply STOP to opt out, HELP for help.';

  function ls(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
  function lsSet(k, v) { try { localStorage.setItem(k, v); } catch (e) {} }
  function done() { return ls(LS_DONE) === '1'; }
  function snoozed() { var t = parseInt(ls(LS_SNOOZE) || '0', 10); return t > 0 && (Date.now() - t) < SNOOZE_MS; }

  function cvr(eventType, props) {
    try {
      var body = {
        event_type: eventType, ts: new Date().toISOString(),
        shop_host: location.host, page_type: 'signup', page_path: location.pathname,
        properties: props || {}
      };
      fetch(CVR, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), keepalive: true }).catch(function () {});
    } catch (e) {}
  }
  function pixelLead(surface) {
    try { if (typeof window.fbq === 'function') window.fbq('track', 'Lead', { content_name: 'cm_signup_' + surface, source: 'convomesh' }); } catch (e) {}
  }

  function submit(payload) {
    return fetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload), keepalive: true })
      .then(function (r) { return r.json(); });
  }
  // ── Inferred-lifestage context (belt-and-suspenders lifestage stamp) ─────────
  // We give the signup the one real signal it otherwise throws away: what's in the
  // cart (matches the slideout's own inferLifestage), or which product's Mum Club
  // button they clicked. The backend stamps it as an INFERRED source — never over a
  // manual/declared stage. No cart/theme coupling: we read /cart.js + the URL here.
  function cartInfer() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) {
        if (!c || !c.items || !c.items.length) return null;
        var hasMat = false, hasPP = false;
        c.items.forEach(function (it) {
          var t = String(it.product_title || it.title || '').toLowerCase();
          if (t.indexOf('maternity') >= 0 || t.indexOf('mat ') >= 0) hasMat = true;
          if (t.indexOf('recovery') >= 0 || t.indexOf('nursing') >= 0) hasPP = true;
        });
        return hasMat ? 'pregnant' : (hasPP ? 'postpartum' : 'mum'); // mirrors cart-set-view inferLifestage
      })
      .catch(function () { return null; });
  }
  function pdpHandle() { var m = location.pathname.match(/\/products\/([a-z0-9-]+)/i); return m ? m[1] : null; }
  function inferCtx(surface) {
    if (surface === 'cart-inline') return cartInfer().then(function (ls) { return ls ? { inferredLifestage: ls } : {}; });
    var h = pdpHandle();                                        // Mum Club on a PDP → the product they're viewing
    return Promise.resolve(h ? { productHandle: h } : {});
  }

  function bank(email, surface) {
    return inferCtx(surface).then(function (ctx) {
      var payload = { email: email, consentEmail: true, surface: surface, consentText: surface === 'cart-inline' ? CONSENT_TEXT_INLINE : CONSENT_TEXT_MODAL };
      if (ctx.inferredLifestage) payload.inferredLifestage = ctx.inferredLifestage;
      if (ctx.productHandle) payload.productHandle = ctx.productHandle;
      return submit(payload);
    })
      .then(function (d) {
        if (d && d.ok) {
          lsSet(LS_DONE, '1');
          window.__cmSigEmail = email;
          cvr('email_capture', { surface: surface });
          pixelLead(surface);
          removeTab();
          normalizeBadges();
          return true;
        }
        return false;
      });
  }

  // ── styles ─────────────────────────────────────────────────────────────────
  var css = ''
    + '.cm-sig-inline{margin-top:8px;}'
    + '.cm-sig-row{display:flex;gap:6px;margin-top:6px;}'
    + '.cm-sig-row input[type=email]{flex:1;min-width:0;padding:9px 10px;border:1px solid #d1d5db;border-radius:8px;font-size:13px;color:#1f2937;background:#fff;}'
    + '.cm-sig-apply{padding:9px 14px;border:0;border-radius:8px;background:#1f2937;color:#fff;font-size:12px;font-weight:700;letter-spacing:.4px;cursor:pointer;}'
    + '.cm-sig-apply[disabled]{opacity:.45;cursor:default;}'
    + '.cm-sig-consent{display:flex;align-items:center;gap:6px;margin-top:6px;font-size:11.5px;color:#6b7280;}'
    + '.cm-sig-consent input{margin:0;}'
    + '.cm-sig-ok{margin-top:8px;font-size:12.5px;font-weight:700;color:#15803d;}'
    + '.cm-sig-err{margin-top:6px;font-size:11.5px;color:#b91c1c;}'
    + '.cm-tab{position:fixed;left:0;top:50%;transform:translateY(-50%);z-index:9990;display:flex;flex-direction:column;align-items:center;background:#1f2937;color:#fff;border-radius:0 8px 8px 0;cursor:pointer;border:0;padding:0 0 12px;}'
    + '.cm-tab-x{background:none;border:0;color:#9ca3af;font-size:15px;line-height:1;padding:8px 10px 6px;cursor:pointer;display:block;}'
    + '.cm-tab-x:hover{color:#fff;}'
    + '.cm-tab-label{writing-mode:vertical-rl;text-orientation:mixed;font-size:12px;font-weight:700;letter-spacing:.5px;line-height:1;padding:2px 8px 0;}'
    + '.cm-modal-ov{position:fixed;inset:0;background:rgba(17,24,39,.55);z-index:100010;display:flex;align-items:center;justify-content:center;padding:16px;}'
    + '.cm-modal{background:#fff;border-radius:14px;max-width:400px;width:100%;padding:22px 20px;position:relative;font-family:inherit;box-shadow:0 18px 50px rgba(0,0,0,.28);}'
    + '.cm-modal h3{margin:0 0 6px;font-size:19px;line-height:1.25;color:#1f2937;}'
    + '.cm-modal p{margin:0 0 12px;font-size:13.5px;line-height:1.5;color:#4b5563;}'
    + '.cm-modal .cm-x{position:absolute;top:8px;right:10px;background:none;border:0;font-size:20px;color:#9ca3af;cursor:pointer;padding:4px;}'
    + '.cm-modal input[type=email],.cm-modal input[type=text],.cm-modal input[type=tel],.cm-modal input[type=date]{width:100%;box-sizing:border-box;padding:10px 11px;border:1px solid #d1d5db;border-radius:8px;font-size:14px;color:#1f2937;background:#fff;margin-top:8px;}'
    + '.cm-modal .cm-go{width:100%;margin-top:10px;padding:12px;border:0;border-radius:8px;background:#1f2937;color:#fff;font-size:14px;font-weight:700;cursor:pointer;}'
    + '.cm-modal .cm-go[disabled]{opacity:.45;cursor:default;}'
    + '.cm-modal .cm-skip{display:block;margin:10px auto 0;background:none;border:0;font-size:12px;color:#9ca3af;text-decoration:underline;cursor:pointer;}'
    + '.cm-modal label.cm-ck{display:flex;align-items:flex-start;gap:7px;margin-top:9px;font-size:12px;color:#6b7280;line-height:1.4;}'
    + '.cm-modal label.cm-ck input{margin:2px 0 0;}'
    + '.cm-seg{display:flex;gap:6px;margin-top:8px;}'
    + '.cm-seg-btn,.cm-age-btn{flex:1;padding:9px 6px;border:1px solid #d1d5db;border-radius:8px;background:#fff;font-size:12.5px;font-weight:600;color:#4b5563;cursor:pointer;text-align:center;}'
    + '.cm-seg-btn.selected,.cm-age-btn.selected{background:#1f2937;color:#fff;border-color:#1f2937;}'
    + '.cm-age-row{display:flex;gap:6px;margin-top:8px;}'
    + '.cm-stage-hint{font-size:11.5px;color:#9ca3af;margin:6px 0 0;}';
  var st = document.createElement('style'); st.textContent = css; document.head.appendChild(st);

  // ── 1. cart-drawer inline (markup provided to scv-drawer.js) ───────────────
  function inlineHtml() {
    if (done()) return '';
    var v = window.__cmSigEmailVal ? String(window.__cmSigEmailVal).replace(/"/g, '&quot;') : '';
    return '<div class="cm-sig-inline" data-cm-sig-shown="1">'
      + '<div class="cm-sig-row">'
      + '<input type="email" id="cm-sig-email" placeholder="enter email for mum club prices" autocomplete="email" value="' + v + '">'
      + '<button type="button" class="cm-sig-apply" id="cm-sig-apply">APPLY</button>'
      + '</div>'
      + '<label class="cm-sig-consent"><input type="checkbox" id="cm-sig-consent" checked> ' + CONSENT_TEXT_INLINE + '</label>'
      + '<div class="cm-sig-err" id="cm-sig-err" style="display:none;"></div>'
      + '</div>';
  }
  function active() { return !done(); }

  var inlineShownFired = false;
  document.addEventListener('input', function (e) {
    if (e.target && e.target.id === 'cm-sig-email') window.__cmSigEmailVal = e.target.value;
  });
  document.addEventListener('change', function (e) {
    if (e.target && e.target.id === 'cm-sig-consent') {
      var btn = document.getElementById('cm-sig-apply');
      if (btn) btn.disabled = !e.target.checked;
    }
  });
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (t && t.id === 'cm-sig-apply') {
      var emailEl = document.getElementById('cm-sig-email');
      var ckEl = document.getElementById('cm-sig-consent');
      var errEl = document.getElementById('cm-sig-err');
      var email = (emailEl && emailEl.value || '').trim();
      if (!ckEl || !ckEl.checked) return;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
        if (errEl) { errEl.textContent = 'Please enter a valid email.'; errEl.style.display = 'block'; }
        return;
      }
      t.disabled = true; t.textContent = '...';
      bank(email, 'cart-inline').then(function (ok) {
        var wrap = t.closest('.cm-sig-inline');
        if (ok && wrap) {
          wrap.innerHTML = '<div class="cm-sig-ok">Mum Club unlocked for this order. Check your inbox.</div>';
          setTimeout(function () { openModal(true); }, 600); // step 2 (optional details)
        } else {
          t.disabled = false; t.textContent = 'APPLY';
          if (errEl) { errEl.textContent = 'Something went wrong - please try again.'; errEl.style.display = 'block'; }
        }
      }).catch(function () { t.disabled = false; t.textContent = 'APPLY'; });
    }
  });
  // ── SPA PDP price badge (.cm-mc-badge, rendered by emamaco-packprice.liquid) ──
  // Unsigned: "unlock mum club prices >" button → modal. Signed up: normalize back
  // to the plain "mum club price" badge. SPA product swaps re-render the fragment,
  // so normalization also runs from the MutationObserver below.
  function normalizeBadges() {
    if (!done()) return;
    var els = document.querySelectorAll('.cm-mc-badge');
    for (var i = 0; i < els.length; i++) {
      if (els[i].textContent !== 'mum club price') els[i].textContent = 'mum club price';
      els[i].style.cursor = 'default';
    }
  }
  document.addEventListener('click', function (e) {
    var b = e.target && e.target.closest ? e.target.closest('.cm-mc-badge') : null;
    if (b && !done()) { e.preventDefault(); openModal(false, 'pdp-badge'); }
  });

  // shown event: fire once when the drawer form first appears; badges normalize on SPA re-renders
  var mo = new MutationObserver(function () {
    if (!inlineShownFired && document.querySelector('[data-cm-sig-shown]')) {
      inlineShownFired = true;
      cvr('section_viewed', { section: 'cm-signup', surface: 'cart-inline' });
    }
    normalizeBadges();
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });

  // ── 2. modal (popup + sticky tab target; also step 2 after inline) ─────────
  var modalEl = null;
  function closeModal(dismissed) {
    if (modalEl) { modalEl.remove(); modalEl = null; }
    if (dismissed && !done()) lsSet(LS_SNOOZE, String(Date.now()));
  }
  function step2Html() {
    return '<h3>Tell us where you&rsquo;re at</h3>'
      + '<p>So we pick the right things for you &amp; bub.</p>'
      + '<input type="text" id="cm-m-name" placeholder="First name" autocomplete="given-name">'
      + '<div class="cm-seg" id="cm-stage">'
      +   '<button type="button" class="cm-seg-btn" data-stage="expecting">I&rsquo;m expecting</button>'
      +   '<button type="button" class="cm-seg-btn" data-stage="born">Baby is here</button>'
      + '</div>'
      + '<div id="cm-stage-expecting" style="display:none;">'
      +   '<input type="date" id="cm-m-due" aria-label="Due date">'
      +   '<p class="cm-stage-hint">Your due date unlocks week-by-week picks &amp; the pregnancy tracker.</p>'
      + '</div>'
      + '<div id="cm-stage-born" style="display:none;">'
      +   '<div class="cm-age-row">'
      +     '<button type="button" class="cm-age-btn" data-age="under-1">Under 1</button>'
      +     '<button type="button" class="cm-age-btn" data-age="1-2">1&ndash;2 yrs</button>'
      +     '<button type="button" class="cm-age-btn" data-age="over-2">2+ yrs</button>'
      +   '</div>'
      +   '<p class="cm-stage-hint">How old is your little one?</p>'
      + '</div>'
      + '<input type="tel" id="cm-m-phone" placeholder="Mobile (optional)" autocomplete="tel">'
      + '<label class="cm-ck"><input type="checkbox" id="cm-m-sms"> ' + CONSENT_TEXT_SMS + '</label>'
      + '<button type="button" class="cm-go" id="cm-m-save">Save</button>'
      + '<p style="font-size:10.5px;color:#9ca3af;line-height:1.45;margin:8px 0 0;">' + SMS_DISCLAIMER + '</p>'
      + '<button type="button" class="cm-skip" id="cm-m-skip">No thanks</button>';
  }
  function step1Html(surface) {
    return '<h3>Every mum deserves better pricing</h3>'
      + '<p><strong>Today, mum club is free.</strong></p>'
      + '<ul style="margin:0 0 10px;padding-left:18px;color:#374151;line-height:1.6;font-size:13.5px;">'
      +   '<li>no ongoing subscriptions</li>'
      +   '<li>no monthly payments</li>'
      + '</ul>'
      + '<p style="margin:-2px 0 12px;">just the biggest deals for you</p>'
      + '<input type="email" id="cm-m-email" placeholder="enter your email" autocomplete="email">'
      + '<label class="cm-ck"><input type="checkbox" id="cm-m-consent" checked> ' + CONSENT_TEXT_MODAL + '</label>'
      + '<button type="button" class="cm-go" id="cm-m-go" data-surface="' + surface + '">Unlock mum club</button>'
      + '<div class="cm-sig-err" id="cm-m-err" style="display:none;"></div>';
  }
  function openModal(step2, surface) {
    if (modalEl) return;
    surface = surface || 'popup';
    modalEl = document.createElement('div');
    modalEl.className = 'cm-modal-ov';
    modalEl.innerHTML = '<div class="cm-modal"><button type="button" class="cm-x" aria-label="Close">&times;</button>'
      + (step2 ? step2Html() : step1Html(surface)) + '</div>';
    document.body.appendChild(modalEl);
    if (!step2) cvr('section_viewed', { section: 'cm-signup', surface: surface });
    modalEl.addEventListener('click', function (e) {
      if (e.target === modalEl || e.target.classList.contains('cm-x')) closeModal(!step2);
    });
  }
  document.addEventListener('change', function (e) {
    if (e.target && e.target.id === 'cm-m-consent') {
      var go = document.getElementById('cm-m-go');
      if (go) go.disabled = !e.target.checked;
    }
  });
  // Enter in an email field submits (there is no <form>, so without this the
  // keypress silently does nothing — the classic "typed my email, nothing happened").
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' || !e.target) return;
    if (e.target.id === 'cm-m-email') { e.preventDefault(); var g = document.getElementById('cm-m-go'); if (g) g.click(); }
    if (e.target.id === 'cm-sig-email') { e.preventDefault(); var a = document.getElementById('cm-sig-apply'); if (a) a.click(); }
  });
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t) return;
    if (t.id === 'cm-m-go') {
      var email = (document.getElementById('cm-m-email') || {}).value || '';
      email = email.trim();
      var ck = document.getElementById('cm-m-consent');
      var err = document.getElementById('cm-m-err');
      if (!ck || !ck.checked) return;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
        if (err) { err.textContent = 'Please enter a valid email.'; err.style.display = 'block'; }
        return;
      }
      t.disabled = true; t.textContent = '...';
      bank(email, t.getAttribute('data-surface') || 'popup').then(function (ok) {
        if (ok) {
          var box = modalEl && modalEl.querySelector('.cm-modal');
          if (box) box.innerHTML = '<button type="button" class="cm-x" aria-label="Close">&times;</button>' + step2Html();
          removeTab();
        } else {
          t.disabled = false; t.textContent = 'Unlock mum club';
          if (err) { err.textContent = 'Something went wrong - please try again.'; err.style.display = 'block'; }
        }
      }).catch(function () { t.disabled = false; t.textContent = 'Unlock mum club'; });
    }
    // Stage chooser (expecting / baby-here): toggle the sub-panel + selected state.
    var seg = t.closest ? t.closest('.cm-seg-btn') : null;
    if (seg) {
      var all = seg.parentNode.querySelectorAll('.cm-seg-btn');
      for (var i = 0; i < all.length; i++) all[i].classList.remove('selected');
      seg.classList.add('selected');
      var exp = document.getElementById('cm-stage-expecting'), born = document.getElementById('cm-stage-born');
      if (exp) exp.style.display = seg.getAttribute('data-stage') === 'expecting' ? 'block' : 'none';
      if (born) born.style.display = seg.getAttribute('data-stage') === 'born' ? 'block' : 'none';
      return;
    }
    var ageBtn = t.closest ? t.closest('.cm-age-btn') : null;
    if (ageBtn) {
      var ages = ageBtn.parentNode.querySelectorAll('.cm-age-btn');
      for (var k = 0; k < ages.length; k++) ages[k].classList.remove('selected');
      ageBtn.classList.add('selected');
      return;
    }
    if (t.id === 'cm-m-skip') closeModal(false);
    if (t.id === 'cm-m-save') {
      var email2 = window.__cmSigEmail || '';
      if (!email2) { closeModal(false); return; }
      var name = (document.getElementById('cm-m-name') || {}).value || '';
      var stageSel = document.querySelector('.cm-seg-btn.selected');
      var stage = stageSel ? stageSel.getAttribute('data-stage') : '';
      var due = stage === 'expecting' ? ((document.getElementById('cm-m-due') || {}).value || '') : '';
      var ageSel = stage === 'born' ? document.querySelector('.cm-age-btn.selected') : null;
      var babyAge = ageSel ? ageSel.getAttribute('data-age') : '';
      var phone = (document.getElementById('cm-m-phone') || {}).value || '';
      var sms = !!((document.getElementById('cm-m-sms') || {}).checked);
      t.disabled = true; t.textContent = '...';
      submit({ email: email2, consentEmail: true, firstName: name, dueDate: due, babyAge: babyAge, phone: phone, consentSms: sms, surface: 'details', consentText: sms ? (CONSENT_TEXT_SMS + ' — ' + SMS_DISCLAIMER) : CONSENT_TEXT_MODAL })
        .then(function () {
          var box = modalEl && modalEl.querySelector('.cm-modal');
          if (box) box.innerHTML = '<button type="button" class="cm-x" aria-label="Close">&times;</button><h3>All set.</h3><p>Mum Club pricing is on your order and your picks just got smarter.</p>';
          setTimeout(function () { closeModal(false); }, 2200);
        })
        .catch(function () { closeModal(false); });
    }
  });

  // ── 3. sticky tab ──────────────────────────────────────────────────────────
  var tabEl = null;
  function removeTab() { if (tabEl) { tabEl.remove(); tabEl = null; } }
  function mountTab() {
    if (done() || snoozed() || tabEl) return;
    tabEl = document.createElement('div');
    tabEl.className = 'cm-tab';
    tabEl.setAttribute('role', 'button');
    tabEl.setAttribute('tabindex', '0');
    // The X is INSIDE the bar, spaced before the label — its own easy tap target
    // (dismiss + 30-day snooze); anywhere else on the bar opens the modal.
    tabEl.innerHTML = '<button type="button" class="cm-tab-x" aria-label="Dismiss">&times;</button>'
      + '<span class="cm-tab-label">get 80% off</span>';
    tabEl.addEventListener('click', function (e) {
      if (e.target && e.target.classList.contains('cm-tab-x')) {
        lsSet(LS_SNOOZE, String(Date.now()));
        removeTab();
        return;
      }
      openModal(false, 'sticky-tab');
    });
    document.body.appendChild(tabEl);
  }

  // ── popup triggers: PDP only, 15s on site OR exit-intent ───────────────────
  var popupFired = false;
  function tryPopup() {
    if (popupFired || done() || snoozed() || modalEl) return;
    if (location.pathname.indexOf('/products/') === -1) return;
    popupFired = true;
    openModal(false, 'popup');
  }
  function boot() {
    mountTab();
    normalizeBadges();
    setTimeout(tryPopup, 15000);
    document.addEventListener('mouseout', function (e) {
      if (!e.relatedTarget && e.clientY <= 0) tryPopup(); // desktop exit-intent
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.__cmSignup = { inlineHtml: inlineHtml, active: active, open: function (s) { openModal(false, s || 'manual'); } };
})();
